import React, { useState } from 'react';
import { Menu, Typography, Tag, Alert } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useSidebar } from '../contexts/SidebarContext';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';

const { Title, Paragraph, Text } = Typography;

/**
 * General-purpose Help/documentation surface for InfraConsole. Designed to grow —
 * new topics are just entries in HELP_TOPICS below, each an independent React node.
 * Keep individual topics scoped to "how do I do X in this app" — link out to
 * docs/backlog.md or docs/planning/ for architecture-level detail instead of
 * duplicating it here.
 */

interface HelpTopic {
  key: string;
  title: string;
  content: React.ReactNode;
}

const HELP_TOPICS: HelpTopic[] = [
  {
    key: 'vnc-connect',
    title: 'Connecting to a VNC Session',
    content: (
      <>
        <Title level={3}>Connecting to a VNC Session</Title>
        <Paragraph>
          InfraConsole supports two independent ways to reach a VNC desktop, depending on which
          kind of account the person has. Both open a session in your browser — no separate VNC
          client needed — but they use different underlying mechanisms, so it's worth knowing
          which one applies.
        </Paragraph>

        <Title level={4}>
          <Tag color="blue">Legacy accounts</Tag> Existing server accounts (IC-041)
        </Title>
        <Paragraph>
          For a person with an existing account on one of the legacy servers (the kind tracked on
          the <Text code>Server Accounts</Text> / <Text code>Unique Users</Text> pages, with a VNC
          port already assigned):
        </Paragraph>
        <ol>
          <li>Go to <Text code>Unique Users</Text> and open that person's detail page.</li>
          <li>
            Scroll to the <Text strong>Legacy VNC Session</Text> card and click{' '}
            <Text strong>Open VNC Session</Text>.
          </li>
          <li>
            A new tab opens and connects automatically. If the person has more than one
            VNC-enabled server account, you'll be asked which server to connect to first.
          </li>
        </ol>
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="How this actually works"
          description={
            <>
              InfraConsole's own backend opens an SSH tunnel to the target server (the VNC port is
              only reachable from that server's own loopback, same as the manual{' '}
              <Text code>ssh -L ...</Text> instructions this replaces) and relays the session to
              your browser over WebSocket. If the target server is unreachable, you'll see a clear
              connection error rather than a blank screen — it's checked live at the moment you
              click Open, not from a cached status.
            </>
          }
        />
        <Paragraph>
          <Text type="secondary">
            Access is currently limited to operator/admin roles (itoperator, itadmin, superadmin).
          </Text>
        </Paragraph>

        <Title level={4}>
          <Tag color="purple">Workspace sessions</Tag> k3s / container-based sessions
        </Title>
        <Paragraph>
          For a person entitled to a workspace-container session (a batch/group instance under{' '}
          <Text code>Workspace → Sessions</Text>), the browser desktop is served by the separate
          Infra Workspace Manager (IWM) system, via its participant portal — reached through its
          own login, not through this console. See{' '}
          <Text code>Workspace → Group Instances</Text> and{' '}
          <Text code>Workspace → Sessions</Text> for the operator-facing session management
          screens in InfraConsole; the actual desktop connection for a participant happens outside
          this app.
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Architecture note for operators: neither VNC path connects your browser directly to the
          target — both relay through a fixed host (InfraConsole's own backend for legacy
          sessions; IWM's participant portal for workspace sessions). See{' '}
          <Text code>docs/backlog.md</Text> (IC-041, IC-045) in the repository for the full
          data-path writeup, including why that's necessary and what it means for scaling to many
          concurrent sessions.
        </Paragraph>
      </>
    ),
  },
];

export default function HelpPage() {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const [activeKey, setActiveKey] = useState<string>(HELP_TOPICS[0]?.key);

  const active = HELP_TOPICS.find((t) => t.key === activeKey) ?? HELP_TOPICS[0];

  return (
    <div className={`flex-1 p-6 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
      {useNew ? (
        <PageHeader title="Help" purpose="How-to guides for InfraConsole features." />
      ) : (
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-800 mb-1 flex items-center gap-2">
            <QuestionCircleOutlined /> Help
          </h1>
          <p className="text-sm text-gray-500">How-to guides for InfraConsole features.</p>
        </div>
      )}

      <div className="flex gap-6">
        <Menu
          mode="inline"
          selectedKeys={[active?.key ?? '']}
          onClick={(e) => setActiveKey(e.key)}
          style={{ width: 260, flexShrink: 0 }}
          items={HELP_TOPICS.map((t) => ({ key: t.key, label: t.title }))}
        />
        <div className="flex-1 max-w-3xl bg-white rounded-lg shadow-sm p-6">
          {active ? active.content : <Paragraph type="secondary">No help topics yet.</Paragraph>}
        </div>
      </div>
    </div>
  );
}
